/**
 * Live View API functions
 * Handles live streaming commands and communication with devices
 */

import { getAuthConfigFromStore, getAuthErrorMessage } from '../auth/authUtils';
import { getApiGateway, getIotApiGateway } from '../api/endpointUtils';
import { 
  LIVE_VIEW_ERROR_MESSAGES,
} from './types';

import type {
  PlayLiveViewParams, 
  PlayLiveViewResponse, 
  StopLiveViewParams,
  StopLiveViewResponse,
  LiveDirectionParams,
  LiveDirectionResponse,
  LivePresignURLParams,
  LivePresignURLResponse,
  WebRTCConfig,
  LiveStreamingWorkflow,
  LiveStreamStatus,
  LiveCameraChannel,
  TwoWayStartParams,
  TwoWayStartResponse,
  TwoWayStopParams,
  TwoWayStopResponse
} from './types';

// Track ongoing requests to prevent duplicates
let ongoingPlayLiveViewRequests = new Set<string>();

/**
 * Start live view streaming for a specific device and camera channel
 * Sends PlayLiveView command to the device
 * 
 * @param params - Parameters for live view playback
 * @returns Promise with live view response including stream status and name
 */
export async function playLiveView(params: Omit<PlayLiveViewParams, 'email' | 'user_token'>): Promise<PlayLiveViewResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  // Create unique request identifier
  const requestId = `${params.psn}-${params.param1 || '1'}`;
  
  // Check if request is already in progress
  if (ongoingPlayLiveViewRequests.has(requestId)) {
    throw new Error('PlayLiveView request already in progress for this device');
  }

  try {
    // Mark request as ongoing
    ongoingPlayLiveViewRequests.add(requestId);
    
    // Get IoT API Gateway URL
    const IOT_API_GATEWAY = getIotApiGateway();
    
    const requestBody = {
      command: 'PlayLiveView',
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: params.tokenType || 'web',
      psn: params.psn,
      param1: params.param1 || '1'
    };
    
    const response = await fetch(`${IOT_API_GATEWAY}/IoT/devicecommand`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: PlayLiveViewResponse = await response.json();
    

    // Handle authentication errors
    if (data.resultcode === 'BC_ERR_AUTHENTICATION') {
      throw new Error('Authentication failed');
    }

    // Handle other error codes
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = LIVE_VIEW_ERROR_MESSAGES[data.resultcode as keyof typeof LIVE_VIEW_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    throw error;
  } finally {
    // Always remove from ongoing requests when done
    ongoingPlayLiveViewRequests.delete(requestId);
  }
}

/**
 * Stop live view streaming for a specific device
 * Sends StopLiveView command to the device
 * 
 * @param params - Parameters for stopping live view
 * @returns Promise with stop live view response
 */
export async function stopLiveView(params: Omit<StopLiveViewParams, 'email' | 'user_token'>): Promise<StopLiveViewResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get IoT API Gateway URL
    const IOT_API_GATEWAY = getIotApiGateway();
    
    
    const response = await fetch(`${IOT_API_GATEWAY}/IoT/devicecommand`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify({
        command: 'StopLiveView',
        email: authConfig.userEmail,
        user_token: authConfig.userToken,
        tokenType: params.tokenType || 'web',
        psn: params.psn
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: StopLiveViewResponse = await response.json();
    

    // Handle authentication errors
    if (data.resultcode === 'BC_ERR_AUTHENTICATION') {
      throw new Error('Authentication failed');
    }

    // Handle other error codes
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = LIVE_VIEW_ERROR_MESSAGES[data.resultcode as keyof typeof LIVE_VIEW_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Change live view camera direction for a specific device
 * Sends LiveDirection command to switch between camera channels
 * Used for multi-camera live view direction switching
 * 
 * @param params - Parameters for camera direction change
 * @returns Promise with direction change response including status and current direction
 */
export async function changeLiveDirection(params: Omit<LiveDirectionParams, 'email' | 'user_token'>): Promise<LiveDirectionResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    
    // Get IoT API Gateway URL
    const IOT_API_GATEWAY = getIotApiGateway();
    
    const requestBody = {
      command: 'LiveDirection',
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      psn: params.psn,
      param1: params.param1
    };
    
    const response = await fetch(`${IOT_API_GATEWAY}/IoT/devicecommand`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: LiveDirectionResponse = await response.json();

    // Handle authentication errors
    if (data.resultcode === 'BC_ERR_AUTHENTICATION') {
      throw new Error('Authentication failed');
    }

    // Handle other error codes
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = LIVE_VIEW_ERROR_MESSAGES[data.resultcode as keyof typeof LIVE_VIEW_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Change live direction with retry mechanism for busy status
 * When status is 'busy', retries the direction change until it succeeds
 * Used to handle multi-user live view scenarios where direction changes may conflict
 * 
 * @param params - Parameters for camera direction change
 * @param maxRetries - Maximum number of retry attempts (default: 5)
 * @param retryInterval - Interval between retries in milliseconds (default: 1000ms)
 * @returns Promise with final direction change response
 */
export async function changeLiveDirectionWithRetry(
  params: Omit<LiveDirectionParams, 'email' | 'user_token'>,
  maxRetries: number = 1,
  retryInterval: number = 3000
): Promise<LiveDirectionResponse> {
  let attempts = 0;
  
  while (attempts < maxRetries) {
    try {
      const response = await changeLiveDirection(params);
      
      // If status is busy, wait and retry
      if (response.status === 'busy') {
        
        if (attempts < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryInterval));
          attempts++;
          continue;
        }
      }
      
      // Return successful response or final attempt
      return response;
      
    } catch (error) {
      // If it's the last attempt, throw the error
      if (attempts >= maxRetries - 1) {
        throw error;
      }
      
      await new Promise(resolve => setTimeout(resolve, retryInterval));
      attempts++;
    }
  }
  
  throw new Error(`Failed to change live direction after ${maxRetries} attempts`);
}

/**
 * Handle LiveClosing status with retry mechanism
 * When device is in LiveClosing state, client should retry PlayLiveView 
 * with 1-2 second intervals until closing state ends
 * 
 * @param params - Parameters for live view playback
 * @param maxRetries - Maximum number of retry attempts (default: 8)
 * @param retryInterval - Interval between retries in milliseconds (default: 2000ms)
 * @returns Promise with final live view response
 */
export async function playLiveViewWithRetry(
  params: Omit<PlayLiveViewParams, 'email' | 'user_token'>,
  maxRetries: number = 8,
  retryInterval: number = 2000
): Promise<PlayLiveViewResponse> {
  let attempts = 0;
  
  while (attempts < maxRetries) {
    try {
      const response = await playLiveView(params);
      
      // Return response regardless of status (including LiveClosing)
      return response;
      
    } catch (error) {
      // If it's the last attempt, throw the error
      if (attempts >= maxRetries - 1) {
        throw error;
      }
      
      await new Promise(resolve => setTimeout(resolve, retryInterval));
      attempts++;
    }
  }
  
  throw new Error(`Failed to start live view after ${maxRetries} attempts`);
}

/**
 * Get WebRTC presigned URL for live streaming
 * This function gets the presigned URL and WebRTC configuration needed for actual video streaming
 * Should only be called when live view status is 'LiveStopped'
 * 
 * @param params - Parameters for presigned URL request
 * @returns Promise with WebRTC configuration for streaming
 */
export async function getLivePresignURL(params: LivePresignURLParams): Promise<LivePresignURLResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL
    const API_GATEWAY = getApiGateway();
    
    
    const response = await fetch(`${API_GATEWAY}/CloudService/LivePresignURL`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(params)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: LivePresignURLResponse = await response.json();
    

    // Handle error codes
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = data.resultcode === 'BC_ERR_TIMEOUT' 
        ? 'Request timeout - please try again'
        : data.resultcode === 'BC_ERR_AUTHENTICATION'
        ? 'Authentication failed'
        : data.resultcode === 'BC_ERR_SERVER'
        ? 'Internal server error'
        : data.message || 'Unknown error occurred';
      
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Complete live streaming workflow
 * Handles the full process: check status -> start live view -> get presigned URL
 * Includes retry logic for LiveClosing status and busy conditions
 * Stream name is generated as PSN + '_live' for presigned URL request
 * 
 * @param psn - Device PSN
 * @param channel - Camera channel
 * @param clientId - Client identifier (usually PSN)
 * @param role - WebRTC role (VIEWER or MASTER)
 * @returns Promise with complete WebRTC configuration
 */
export async function startLiveStreamingWorkflow(
  psn: string,
  channel: LiveCameraChannel = '1',
  clientId?: string,
  role: 'VIEWER' | 'MASTER' = 'VIEWER'
): Promise<WebRTCConfig> {
  
  try {
    // Step 1: Start live view with retry for LiveClosing
    const liveViewResponse = await playLiveView({ psn, param1: channel });
        
    // Check if device is busy with another stream
    if (liveViewResponse.status === 'onLiveStreaming') {
      throw new Error('Device is currently streaming to another client. Please try again later.');
    }
    
    // Generate stream name from PSN for presigned URL request
    const streamName = `${psn}_live`;
    
    // Step 2: Get presigned URL for WebRTC
    const presignResponse = await getLivePresignURL({
      streamName: streamName,
      role: role,
      psn: psn
    });
    
    // Validate presigned URL response
    if (!presignResponse.region || !presignResponse.channelARN || !presignResponse.url || 
        !presignResponse.iceServers || !presignResponse.endpointsByProtocol) {
      throw new Error('Incomplete WebRTC configuration received');
    }
    
    // Step 3: Create WebRTC configuration
    const webrtcConfig: WebRTCConfig = {
      region: presignResponse.region,
      channelARN: presignResponse.channelARN,
      url: presignResponse.url,
      iceServers: presignResponse.iceServers,
      endpointsByProtocol: presignResponse.endpointsByProtocol,
      role: role,
      clientId: clientId || psn
    };
    
    return webrtcConfig;
    
  } catch (error) {
    throw error;
  }
}

/**
 * Create custom signer for WebRTC SignalingClient
 * Used to provide presigned URL to KVS WebRTC SignalingClient
 * 
 * @param url - Presigned WebSocket URL
 * @returns Custom signer object for SignalingClient
 */
export function createCustomSigner(url: string) {
  return {
    getSignedURL: () => url
  };
}

/**
 * Validate WebRTC configuration
 * Checks if all required fields are present and valid
 * 
 * @param config - WebRTC configuration to validate
 * @returns Boolean indicating if configuration is valid
 */
export function validateWebRTCConfig(config: WebRTCConfig): boolean {
  const required = [
    config.region,
    config.channelARN,
    config.url,
    config.iceServers,
    config.endpointsByProtocol,
    config.role,
    config.clientId
  ];
  
  const isValid = required.every(field => field !== undefined && field !== null);
  
  return isValid;
}
/**
 * Get live stream status for a device
 * Utility function to check current streaming status
 * 
 * @param psn - Device PSN
 * @param channel - Camera channel (optional, defaults to '1')
 * @returns Promise with current live stream status
 */
export async function getLiveStreamStatus(
  psn: string, 
  channel: LiveCameraChannel = '1'
): Promise<{ status: LiveStreamStatus; streamname?: string }> {
  try {
    const response = await playLiveView({ psn, param1: channel });
    return {
      status: response.status || 'free',
      streamname: response.streamname
    };
  } catch (error) {
    throw error;
  }
}

/**
 * Start two-way audio communication with device
 * Sends TwoWayStart command to enable bidirectional audio
 * 
 * @param params - Parameters for starting two-way audio
 * @returns Promise with two-way start response including status
 */
export async function startTwoWayAudio(params: Omit<TwoWayStartParams, 'email' | 'user_token'>): Promise<TwoWayStartResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    
    // Get IoT API Gateway URL
    const IOT_API_GATEWAY = getIotApiGateway();
    
    const requestBody = {
      command: 'TwoWayStart',
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: params.tokenType || 'web',
      psn: params.psn
    };
    
    const response = await fetch(`${IOT_API_GATEWAY}/IoT/devicecommand`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: TwoWayStartResponse = await response.json();

    // Handle authentication errors
    if (data.resultcode === 'BC_ERR_AUTHENTICATION') {
      throw new Error('Authentication failed');
    }

    // Handle other error codes
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = LIVE_VIEW_ERROR_MESSAGES[data.resultcode as keyof typeof LIVE_VIEW_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Stop two-way audio communication with device
 * Sends TwoWayStop command to disable bidirectional audio
 * 
 * @param params - Parameters for stopping two-way audio
 * @returns Promise with two-way stop response including status
 */
export async function stopTwoWayAudio(params: Omit<TwoWayStopParams, 'email' | 'user_token'>): Promise<TwoWayStopResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    
    // Get IoT API Gateway URL
    const IOT_API_GATEWAY = getIotApiGateway();
    
    const requestBody = {
      command: 'TwoWayStop',
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: params.tokenType || 'web',
      psn: params.psn
    };
    
    const response = await fetch(`${IOT_API_GATEWAY}/IoT/devicecommand`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: TwoWayStopResponse = await response.json();

    // Handle authentication errors
    if (data.resultcode === 'BC_ERR_AUTHENTICATION') {
      throw new Error('Authentication failed');
    }

    // Handle other error codes
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = LIVE_VIEW_ERROR_MESSAGES[data.resultcode as keyof typeof LIVE_VIEW_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    throw error;
  }
}